import { cn } from "../../../utils/classnames";
import { SortIcon } from "./Icons";

/**
 * Shared table chrome for the admin list pages.
 *
 * The booking list is the reference design: dark header row, 75px white rows
 * separated by a hairline, an optional sticky action column on the right and a
 * skeleton state that keeps the row rhythm while data loads.
 *
 * Columns are declared as
 *   { key, title, width, sortField, sticky, align, skeleton }
 * and the rows are supplied as children so each page keeps rendering its own
 * cells (with `cellClass` / `stickyCellClass` below).
 */

export const cellClass =
  "tw:h-[75px] tw:border-b tw:border-[#F3F3F3] tw:bg-white tw:px-[15px] tw:text-[15px] tw:leading-[22px] tw:align-middle";

export const stickyCellClass =
  "tw:sticky tw:right-0 tw:z-10 tw:h-[75px] tw:border-b tw:border-x tw:border-[#F3F3F3] tw:bg-white tw:px-[15px] tw:text-center tw:align-middle";

const headerCellClass =
  "tw:h-[60px] tw:bg-[#282828] tw:px-[15px] tw:py-[20px] tw:text-left tw:text-[15px] tw:font-bold tw:leading-[22px] tw:text-white";

const HeaderCell = ({ column, sortConfig, onSort }) => {
  const sortable = !!column.sortField && typeof onSort === "function";
  const direction = sortConfig?.key === column.sortField ? sortConfig?.direction : null;

  return (
    <th
      className={cn(
        headerCellClass,
        column.width,
        column.align === "center" && "tw:text-center",
        sortable && "tw:cursor-pointer tw:select-none",
        column.sticky && "tw:sticky tw:right-0 tw:z-20",
      )}
      onClick={sortable ? () => onSort(column.sortField) : undefined}
    >
      <div
        className={cn(
          "tw:flex tw:items-center",
          sortable && "tw:justify-between",
          column.align === "center" && !sortable && "tw:justify-center",
        )}
      >
        <span className="tw:whitespace-nowrap">{column.title}</span>
        {sortable ? <SortIcon direction={direction} /> : null}
      </div>
    </th>
  );
};

const SkeletonBar = ({ className }) => (
  <span className={cn("tw:block tw:h-[16px] tw:animate-pulse tw:rounded-[4px] tw:bg-[#EDEDED]", className)} />
);

const SkeletonCell = ({ column }) => {
  switch (column.skeleton || (column.sticky ? "action" : undefined)) {
    case "badge":
      return <SkeletonBar className="tw:h-[40px] tw:w-full tw:rounded-[5px]" />;
    case "stacked":
      return (
        <div className="tw:flex tw:flex-col tw:gap-[8px]">
          <SkeletonBar className="tw:w-[70%]" />
          <SkeletonBar className="tw:h-[12px] tw:w-[45%]" />
        </div>
      );
    case "action":
      return <SkeletonBar className="tw:mx-auto tw:h-[30px] tw:w-[30px] tw:rounded-[2px]" />;
    case "narrow":
      return <SkeletonBar className="tw:w-[50%]" />;
    default:
      return <SkeletonBar className="tw:w-[80%]" />;
  }
};

const SkeletonRows = ({ columns, rowCount }) =>
  Array.from({ length: rowCount }).map((_, rowIndex) => (
    <tr key={`skeleton-${rowIndex}`}>
      {columns.map((column) => (
        <td key={column.key} className={column.sticky ? stickyCellClass : cellClass}>
          <SkeletonCell column={column} />
        </td>
      ))}
    </tr>
  ));

const AdminTable = ({
  columns,
  minWidth,
  sortConfig,
  onSort,
  loading = false,
  skeletonRowCount = 10,
  isEmpty = false,
  emptyText = "No records found.",
  className,
  children,
}) => (
  <section className={cn("tw:mt-[34px] tw:w-full tw:overflow-x-auto", className)}>
    <table
      className="tw:w-full tw:border-separate tw:border-spacing-0"
      style={minWidth ? { minWidth: `${minWidth}px` } : undefined}
    >
      <thead>
        <tr>
          {columns.map((column) => (
            <HeaderCell key={column.key} column={column} sortConfig={sortConfig} onSort={onSort} />
          ))}
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <SkeletonRows columns={columns} rowCount={skeletonRowCount} />
        ) : isEmpty ? (
          <tr>
            <td colSpan={columns.length} className={`${cellClass} tw:text-center tw:!text-[#7C7B7B]`}>
              {emptyText}
            </td>
          </tr>
        ) : (
          children
        )}
      </tbody>
    </table>
  </section>
);

export default AdminTable;
